/**
 * Servicio admin para colección raíz `campanas`.
 * Status: 'activa' | 'pausada' | 'eliminada'. Si no existe, se trata como 'activa' en UI.
 */

import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { enriquecerConNombreEmpresa } from '../../empresa/services/empresaNombreHelper';

const COL = 'campanas';
const DEFAULT_LIMIT = 100;

/** Mapper: título para listado */
export function mapCampanaTitle(docData) {
  return docData?.titulo ?? docData?.nombre ?? docData?.title ?? '(Sin título)';
}

/** Mapper: empresa para listado */
export function mapCampanaEmpresa(docData) {
  return docData?.empresaNombre ?? docData?.empresaId ?? '—';
}

/** Normaliza status */
export function normalizeStatus(docData) {
  const s = docData?.status ?? docData?.estado;
  if (s === 'pausada' || s === 'eliminada') return s;
  return 'activa';
}

/**
 * Lista campañas con filtro opcional por status.
 */
export async function fetchCampanas(opts = {}) {
  const { statusFilter = 'activa_pausada', limit: limitNum = DEFAULT_LIMIT } = opts;
  const col = collection(db, COL);

  let list = [];
  try {
    const q = query(col, orderBy('fechaCreacion', 'desc'), limit(limitNum));
    const snap = await getDocs(q);
    list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    try {
      const q = query(col, orderBy('updatedAt', 'desc'), limit(limitNum));
      const snap = await getDocs(q);
      list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e2) {
      const snap = await getDocs(col);
      list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.fechaCreacion?.toMillis?.() ?? a.updatedAt?.toMillis?.() ?? 0;
        const tb = b.fechaCreacion?.toMillis?.() ?? b.updatedAt?.toMillis?.() ?? 0;
        return tb - ta;
      });
      list = list.slice(0, limitNum);
    }
  }

  list = list.map((item) => ({
    ...item,
    _displayTitle: mapCampanaTitle(item),
    _displayEmpresa: mapCampanaEmpresa(item),
    _status: normalizeStatus(item),
  }));

  await enriquecerConNombreEmpresa(list);

  if (statusFilter === 'activa') list = list.filter((c) => c._status === 'activa');
  else if (statusFilter === 'pausada') list = list.filter((c) => c._status === 'pausada');
  else if (statusFilter === 'eliminada') list = list.filter((c) => c._status === 'eliminada');
  else if (statusFilter === 'activa_pausada') list = list.filter((c) => c._status === 'activa' || c._status === 'pausada');

  return list;
}

export async function updateCampana(id, patch) {
  const ref = doc(db, COL, id);
  const data = { ...patch, updatedAt: serverTimestamp() };
  if (patch.titulo != null) data.tituloLower = String(patch.titulo).toLowerCase().trim();
  if (patch.nombre != null) data.tituloLower = String(patch.nombre).toLowerCase().trim();
  await updateDoc(ref, data);
}

export async function toggleCampanaPause(id, currentStatus, userId) {
  const ref = doc(db, COL, id);
  if (currentStatus === 'pausada') {
    await updateDoc(ref, {
      status: 'activa',
      resumedAt: serverTimestamp(),
      resumedBy: userId ?? null,
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(ref, {
      status: 'pausada',
      pausedAt: serverTimestamp(),
      pausedBy: userId ?? null,
      updatedAt: serverTimestamp(),
    });
  }
}

export async function softDeleteCampana(id, userId) {
  const ref = doc(db, COL, id);
  await updateDoc(ref, {
    status: 'eliminada',
    deletedAt: serverTimestamp(),
    deletedBy: userId ?? null,
    updatedAt: serverTimestamp(),
  });
}
